/**
 * Export Module — JPEG/PNG/Video/PDF с аннотациями и оверлеями
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Функционал:
 *   ✓ Скриншот с оверлеями (patient info, W/L, модальность) — composite canvas
 *   ✓ Серия как видео MP4/WebM (MediaRecorder API)
 *   ✓ Все вьюпорты как PDF (jsPDF)
 *   ✓ Пакетный экспорт серии как JPEG/PNG ZIP
 *   ✓ Анонимный режим (скрытие PII)
 *   ✓ Настраиваемое качество и разрешение
 *   ✓ Watermark / Copyright stamp
 */

import { openPrintWindow } from './printUtil.js';   // [11.1.32] звался с 11.1.18 БЕЗ ИМПОРТА: запасной путь печати падал бы ReferenceError. Нашёл сторож tests/undeclared_check.mjs
import { cornerstone, cornerstoneTools } from '../core/init.js';
import { getRenderingEngine, getViewportIds, getViewportElement } from '../core/viewport.js';
import { downloadBlob, showToast, escapeHtml } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();


const $ = (id) => document.getElementById(id);

let _ctx = null;

// ══════════════════════════════════════════════════════════════
// PUBLIC API
// ══════════════════════════════════════════════════════════════

export function initExportModule(ctx) {
  _ctx = ctx;
  _bindButtons();
}

// ══════════════════════════════════════════════════════════════
// 1. SCREENSHOT — viewport + overlays + annotations
// ══════════════════════════════════════════════════════════════

/**
 * Экспорт текущего вьюпорта как изображение с оверлеями.
 * @param {Object} opts
 * @param {string} [opts.format='png'] — 'png' | 'jpeg'
 * @param {number} [opts.quality=0.95] — JPEG quality
 * @param {boolean} [opts.anonymous=false] — скрыть PII
 * @param {boolean} [opts.includeOverlays=true] — включить текстовые оверлеи
 * @param {string} [opts.watermark=''] — текст водяного знака
 * @param {string} [opts.viewportId] — ID вьюпорта (default: текущий)
 * @returns {Promise<Blob>}
 */
export async function captureViewport(opts = {}) {
  try {
    const {
      format = 'png',
      quality = 0.95,
      anonymous = false,
      includeOverlays = true,
      watermark = '',
      viewportId,
    } = opts;
  
    const vpId = viewportId || _ctx?.getCurrentViewportId?.() || getViewportIds()[0];
    const vpEl = $(`viewport-${vpId}`) || $('viewport-container');
    if (!vpEl) throw new Error('Viewport element not found');
  
    // Находим WebGL canvas (с аннотациями cornerstoneTools)
    const glCanvas = vpEl.querySelector('canvas');
    if (!glCanvas) throw new Error('Canvas not found');
  
    const w = glCanvas.width;
    const h = glCanvas.height;
  
    // Создаём composite canvas
    const composite = document.createElement('canvas');
    composite.width = w;
    composite.height = h;
    const ctx2d = composite.getContext('2d');
  
    // 1. Рисуем основной viewport canvas (включает аннотации CS Tools)
    ctx2d.drawImage(glCanvas, 0, 0);
  
    // 2. Рисуем HTML оверлеи поверх
    if (includeOverlays) {
      _renderOverlays(ctx2d, vpEl, w, h, anonymous);
    }
  
    // 3. Watermark
    if (watermark) {
      _renderWatermark(ctx2d, watermark, w, h);
    }
  
    // 4. Timestamp
    _renderTimestamp(ctx2d, w, h);
  
    const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
    return new Promise((resolve) => composite.toBlob(resolve, mimeType, quality));
  } catch (e) { log.warn(`[exportModule] captureViewport failed:`, e.message); throw e; }
}

/**
 * Скачать скриншот вьюпорта.
 */
export async function downloadScreenshot(opts = {}) {
  отметитьЭкспорт('снимок', opts?.filename || opts?.name || '');
  try {
    const blob = await captureViewport(opts);
    const ext = opts.format === 'jpeg' ? 'jpg' : 'png';
    const prefix = opts.anonymous ? 'anon_' : '';
    downloadBlob(blob, `mikhmel_${prefix}${Date.now()}.${ext}`);
    showToast(opts.anonymous ? '📷 Анонимный снимок сохранён' : '📷 Снимок сохранён', 'success');
    return blob;
  } catch (e) {
    showToast(`❌ Ошибка снимка: ${e.message}`, 'error');
    log.error('[Export]', e);
  }
}

// ══════════════════════════════════════════════════════════════
// 2. VIDEO — запись серии или вращения как MP4/WebM
// ══════════════════════════════════════════════════════════════

let _mediaRecorder = null;
let _recordedChunks = [];
let _isRecording = false;

/**
 * Запись вьюпорта в видео.
 * @param {Object} opts
 * @param {string} [opts.mode='scroll'] — 'scroll' (прокрутка серии) | 'rotate' (вращение 3D) | 'manual' (ручной)
 * @param {number} [opts.fps=15] — частота кадров
 * @param {number} [opts.duration=10] — длительность для rotate (сек)
 * @param {string} [opts.format='webm'] — 'webm' | 'mp4' (через webm→mp4 конвертацию)
 * @param {boolean} [opts.anonymous=false]
 */
export async function startVideoRecording(opts = {}) {
  try {
    const {
      mode = 'scroll',
      fps = 15,
      duration = 10,
      anonymous = false,
    } = opts;
  
    if (_isRecording) {
      showToast('⚠️ Запись уже идёт', 'warning');
      return;
    }
  
    const vpId = _ctx?.getCurrentViewportId?.() || getViewportIds()[0];
    const vpEl = $(`viewport-${vpId}`) || $('viewport-container');
    const glCanvas = vpEl?.querySelector('canvas');
    if (!glCanvas) { showToast('❌ Холст не найден', 'error'); return; }
  
    _recordedChunks = [];
    _isRecording = true;
  
    // Создаём offscreen canvas для composite (overlays + watermark)
    const offscreen = document.createElement('canvas');
    offscreen.width = glCanvas.width;
    offscreen.height = glCanvas.height;
    const offCtx = offscreen.getContext('2d');
  
    // captureStream от offscreen canvas
    const stream = offscreen.captureStream(fps);
  
    // MediaRecorder
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : 'video/mp4';
  
    _mediaRecorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: 5_000_000,
    });
  
    _mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) _recordedChunks.push(e.data);
    };
  
    _mediaRecorder.onstop = () => {
      const blob = new Blob(_recordedChunks, { type: mimeType });
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
      downloadBlob(blob, `mikhmel_${mode}_${Date.now()}.${ext}`);
      showToast(`🎥 Видео сохранено (${(_recordedChunks.reduce((s,c)=>s+c.size,0)/1024/1024).toFixed(1)} МБ)`, 'success');
      _isRecording = false;
      _recordedChunks = [];
    };
  
    _mediaRecorder.start(100); // chunk every 100ms
    showToast(`🔴 Запись ${mode}...`, 'info', 2000);
  
    if (mode === 'scroll') {
      await _recordScrollThrough(glCanvas, offscreen, offCtx, vpId, vpEl, fps, anonymous);
      stopVideoRecording();
    } else if (mode === 'rotate') {
      await _recordRotation(glCanvas, offscreen, offCtx, vpEl, duration, fps, anonymous);
      stopVideoRecording();
    }
    // mode === 'manual' — пользователь сам вызовет stopVideoRecording()
  } catch (e) { log.warn(`[exportModule] startVideoRecording failed:`, e.message);  }
}

export function stopVideoRecording() {
  отметитьЭкспорт('видео', '');
  if (_mediaRecorder && _isRecording) {
    _mediaRecorder.stop();
  }
}

export function isRecording() { return _isRecording; }

async function _recordScrollThrough(glCanvas, offscreen, offCtx, vpId, vpEl, fps, anonymous) {
  const re = getRenderingEngine();
  const vp = re.getViewport(vpId);
  if (!vp || !vp.getNumberOfSlices) return;

  const total = vp.getNumberOfSlices?.() || vp.csImage?.imageIds?.length || 100;
  const frameDelay = 1000 / fps;

  for (let i = 0; i < total; i++) {
    if (!_isRecording) break;

    try {
      vp.setImageIdIndex?.(i);
      vp.scroll?.(0); // force render at current index
      vp.render();
    } catch (_) { console.debug('[ExportModule]', _.message); }

    // Wait for render
    await new Promise(r => requestAnimationFrame(r));

    // Composite frame
    offCtx.drawImage(glCanvas, 0, 0);
    if (!anonymous) _renderOverlays(offCtx, vpEl, offscreen.width, offscreen.height, false);
    _renderTimestamp(offCtx, offscreen.width, offscreen.height);

    // Frame counter overlay
    offCtx.fillStyle = 'rgba(0,0,0,0.5)';
    offCtx.fillRect(offscreen.width - 80, offscreen.height - 24, 80, 24);
    offCtx.fillStyle = '#0f0';
    offCtx.font = '12px monospace';
    offCtx.fillText(`${i+1}/${total}`, offscreen.width - 72, offscreen.height - 8);

    await _delay(frameDelay);
  }
}

async function _recordRotation(glCanvas, offscreen, offCtx, vpEl, duration, fps, anonymous) {
  const re = getRenderingEngine();
  const vp3dId = getViewportIds().find(id => id.includes('3d') || id.includes('volume'));
  const vp = vp3dId ? re.getViewport(vp3dId) : null;
  if (!vp) { showToast('⚠️ 3D окно не найден', 'warning'); return; }

  const totalFrames = Math.round(duration * fps);
  const anglePerFrame = 360 / totalFrames;
  const frameDelay = 1000 / fps;

  for (let i = 0; i < totalFrames; i++) {
    if (!_isRecording) break;

    try {
      const camera = vp.getCamera();
      const pos = camera.position;
      const fp = camera.focalPoint;
      const angle = (anglePerFrame * Math.PI) / 180;

      // Rotate around Y axis
      const dx = pos[0] - fp[0];
      const dz = pos[2] - fp[2];
      const newX = fp[0] + dx * Math.cos(angle) - dz * Math.sin(angle);
      const newZ = fp[2] + dx * Math.sin(angle) + dz * Math.cos(angle);

      vp.setCamera({ position: [newX, pos[1], newZ] });
      vp.render();
    } catch (_) { console.debug('[ExportModule]', _.message); }

    await new Promise(r => requestAnimationFrame(r));

    offCtx.drawImage(glCanvas, 0, 0);
    if (!anonymous) _renderOverlays(offCtx, vpEl, offscreen.width, offscreen.height, false);

    await _delay(frameDelay);
  }
}

// ══════════════════════════════════════════════════════════════
// 3. PDF — все вьюпорты или текущий + отчёт
// ══════════════════════════════════════════════════════════════

/**
 * Экспорт в PDF — текущий вьюпорт или все.
 * @param {Object} opts
 * @param {boolean} [opts.allViewports=false]
 * @param {boolean} [opts.anonymous=false]
 * @param {string} [opts.title='DICOM Report']
 * @param {string} [opts.notes='']
 */
export async function exportPDF(opts = {}) {
  отметитьЭкспорт('PDF', opts?.filename || opts?.name || '');
  const {
    allViewports = false,
    anonymous = false,
    title = 'DICOM Report',
    notes = '',
  } = opts;

  showToast('📄 Формирование PDF...', 'info', 2000);

  // [11.1.19] jsPDF ИЗГНАН, и вот почему. Его встроенные шрифты (Helvetica и
  // родня) — WinAnsi без кириллицы: каждый doc.text('Пациент…',
  // 'Заключение…', notes) уходил на бумагу абракадаброй — замечание
  // владельца «все pdf отчёты… вместо текста абракадабра». Чинить можно
  // было вшиванием TTF-шрифта в jsPDF, но у программы УЖЕ есть движок,
  // который печатает кириллицу безупречно, — Chromium (открытьПечатное:
  // printToPDF в настольной сборке, диалог печати в вебе). Держать второй
  // PDF-движок ради того же результата — это два реестра на одно понятие,
  // порода, выкорчеванная в 11.1.17. Теперь у PDF ОДИН движок.
  try {
    const vpIds = allViewports ? getViewportIds() : [_ctx?.getCurrentViewportId?.() || getViewportIds()[0]];
    const кадры = [];
    for (const vpId of vpIds) {
      const blob = await captureViewport({ viewportId: vpId, format: 'jpeg', quality: 0.9, anonymous, includeOverlays: true });
      if (blob) кадры.push(await blobToDataURL(blob));
    }
    if (!кадры.length) { showToast('⚠️ Нечего печатать — окна пусты', 'warning', 2500); return; }

    const patName = anonymous ? '' : ($('overlay-patient')?.textContent || '');
    const studyDate = anonymous ? '' : ($('overlay-date')?.textContent || '');
    const modality = anonymous ? '' : ($('overlay-modality')?.textContent || '');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  body { font-family: 'Times New Roman', serif; color: #111; margin: 0; }
  .head { font-size: 14pt; font-weight: bold; }
  .sub { font-size: 9pt; color: #555; margin-bottom: 4mm; }
  .page { page-break-after: always; }
  .page:last-child { page-break-after: auto; }
  .page img { width: 100%; max-height: 175mm; object-fit: contain; display: block; }
  .notes h2 { font-size: 12pt; border-bottom: 1px solid #999; }
  .notes div { white-space: pre-wrap; font-size: 11pt; }
</style></head><body>
<div class="head">${escapeHtml(title)}</div>
<div class="sub">Mikhmel DICOM Viewer — ${escapeHtml(new Date().toLocaleString('ru-RU'))}${patName ? ` · Пациент: ${escapeHtml(patName)} · Дата: ${escapeHtml(studyDate)} · Модальность: ${escapeHtml(modality)}` : ''}</div>
${кадры.map((у) => `<div class="page"><img src="${у}"></div>`).join('')}
${notes ? `<div class="notes"><h2>Заключение</h2><div>${escapeHtml(notes)}</div></div>` : ''}
</body></html>`;
    await открытьПечатное(html, 'width=980,height=740');
  } catch (e) {
    log.warn('[Export] exportPDF:', e.message);
    showToast('❌ PDF не собрался: ' + e.message, 'error', 4000);
  }
}

// ══════════════════════════════════════════════════════════════
// 4. BATCH EXPORT — серия как ZIP с JPEG
// ══════════════════════════════════════════════════════════════

/**
 * Экспорт всех срезов серии как JPEG в ZIP.
 * @param {Object} opts
 * @param {string} [opts.format='jpeg']
 * @param {number} [opts.quality=0.85]
 * @param {boolean} [opts.anonymous=false]
 */
export async function exportSeriesAsZip(opts = {}) {
  отметитьЭкспорт('ZIP-серия', opts?.filename || opts?.name || '');
  try {
    const {
      format = 'jpeg',
      quality = 0.85,
      anonymous = false,
    } = opts;
  
    const vpId = _ctx?.getCurrentViewportId?.() || getViewportIds()[0];
    const re = getRenderingEngine();
    const vp = re.getViewport(vpId);
    if (!vp) { showToast('❌ Окно не найдено', 'error'); return; }
  
    const total = vp.getNumberOfSlices?.() || 1;
    showToast(`📦 Экспорт ${total} срезов...`, 'info', 3000);
  
    try {
      // [10.19.10] Была загрузка с cdnjs — офлайн не работало, хотя пакет
      // jszip стоит локально и записан в package.json. Берём местный.
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const folder = zip.folder('series');
      const ext = format === 'jpeg' ? 'jpg' : 'png';
      const mime = format === 'jpeg' ? 'image/jpeg' : 'image/png';
  
      const vpEl = $(`viewport-${vpId}`) || $('viewport-container');
      const glCanvas = vpEl?.querySelector('canvas');
  
      for (let i = 0; i < total; i++) {
        try {
          vp.setImageIdIndex?.(i);
          vp.render();
        } catch (_) { console.debug('[ExportModule]', _.message); }
  
        await new Promise(r => requestAnimationFrame(r));
  
        const blob = await new Promise(resolve => glCanvas.toBlob(resolve, mime, quality));
        const ab = await blob.arrayBuffer();
        folder.file(`slice_${String(i + 1).padStart(4, '0')}.${ext}`, ab);
  
        if (i % 20 === 0) {
          _ctx?.updateLoadingOverlay?.(Math.round(i / total * 100), 'Экспорт срезов...', `${i}/${total}`);
        }
      }
  
      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
      downloadBlob(zipBlob, `mikhmel_series_${Date.now()}.zip`);
      showToast(`📦 ZIP сохранён (${total} изображений)`, 'success');
  
    } catch (e) {
      log.warn('[Export] JSZip not available:', e.message);
      showToast('❌ Для ZIP экспорта нужен JSZip', 'error');
    }
  } catch (e) { log.warn(`[exportModule] exportSeriesAsZip failed:`, e.message);  }
}

// ══════════════════════════════════════════════════════════════
// OVERLAY RENDERING — текстовые оверлеи поверх canvas
// ══════════════════════════════════════════════════════════════

function _renderOverlays(ctx2d, vpEl, w, h, anonymous) {
  const overlays = vpEl.querySelectorAll('.vp-overlay');
  if (!overlays.length) return;

  ctx2d.save();

  // Собираем текст из каждого overlay
  const corners = {
    'top-left': [], 'top-right': [],
    'bottom-left': [], 'bottom-right': [],
  };

  for (const ov of overlays) {
    if (ov.style.display === 'none') continue;
    const text = anonymous ? _anonymizeText(ov.textContent) : ov.textContent.trim();
    if (!text) continue;

    // Определяем угол по CSS-классам или стилям
    const style = window.getComputedStyle(ov);
    const top = parseInt(style.top) || 0;
    const left = parseInt(style.left) || 0;
    const right = parseInt(style.right) || 0;
    const bottom = parseInt(style.bottom) || 0;

    let corner = 'top-left';
    if (right < w / 2 && top < h / 2) corner = 'top-right';
    else if (left < w / 2 && bottom < h / 2) corner = 'bottom-left';
    else if (right < w / 2 && bottom < h / 2) corner = 'bottom-right';
    else if (top < h / 2) corner = 'top-left';

    // Fallback: используем классы
    if (ov.classList.contains('top-right') || ov.id?.includes('top-right')) corner = 'top-right';
    if (ov.classList.contains('bottom-left') || ov.id?.includes('bottom-left')) corner = 'bottom-left';
    if (ov.classList.contains('bottom-right') || ov.id?.includes('bottom-right')) corner = 'bottom-right';

    corners[corner].push(text);
  }

  ctx2d.font = '12px system-ui, sans-serif';
  ctx2d.textBaseline = 'top';
  const pad = 8;
  const lineH = 16;

  // Рисуем каждый угол
  for (const [corner, lines] of Object.entries(corners)) {
    if (!lines.length) continue;

    for (let i = 0; i < lines.length; i++) {
      const text = lines[i];
      let x, y;

      if (corner.includes('left')) { x = pad; ctx2d.textAlign = 'left'; }
      else { x = w - pad; ctx2d.textAlign = 'right'; }

      if (corner.includes('top')) { y = pad + i * lineH; }
      else { y = h - pad - (lines.length - i) * lineH; }

      // Тень для читаемости
      ctx2d.fillStyle = 'rgba(0,0,0,0.7)';
      const metrics = ctx2d.measureText(text);
      const bx = corner.includes('left') ? x - 2 : x - metrics.width - 2;
      ctx2d.fillRect(bx, y - 1, metrics.width + 4, lineH);

      ctx2d.fillStyle = '#e0e0e0';
      ctx2d.fillText(text, x, y);
    }
  }

  ctx2d.restore();
}

function _renderWatermark(ctx2d, text, w, h) {
  ctx2d.save();
  ctx2d.globalAlpha = 0.15;
  ctx2d.font = 'bold 48px system-ui';
  ctx2d.fillStyle = '#fff';
  ctx2d.textAlign = 'center';
  ctx2d.textBaseline = 'middle';
  ctx2d.translate(w / 2, h / 2);
  ctx2d.rotate(-Math.PI / 6);
  ctx2d.fillText(text, 0, 0);
  ctx2d.restore();
}

function _renderTimestamp(ctx2d, w, h) {
  const ts = new Date().toLocaleString('ru-RU');
  ctx2d.save();
  ctx2d.font = '10px monospace';
  ctx2d.fillStyle = 'rgba(255,255,255,0.5)';
  ctx2d.textAlign = 'right';
  ctx2d.fillText(ts, w - 4, h - 4);
  ctx2d.restore();
}

function _anonymizeText(text) {
  // Заменяем потенциальные PII
  return text
    .replace(/[А-ЯЁ][а-яё]+\s+[А-ЯЁ][а-яё]*/g, '***') // Русские имена
    .replace(/[A-Z][a-z]+\s+[A-Z][a-z]*/g, '***')         // English names
    .replace(/\d{2}[\.\/-]\d{2}[\.\/-]\d{4}/g, '**.**.****') // Dates
    .replace(/\b\d{4,}\b/g, '****');                         // IDs
}

// ══════════════════════════════════════════════════════════════
// UI BINDINGS
// ══════════════════════════════════════════════════════════════

function _bindButtons() {
  // Перехватываем существующие кнопки
  const btnScreenshot = $('btn-screenshot');
  const btnScreenshotAnon = $('btn-screenshot-anon');
  const btnExportMp4 = $('btn-export-mp4');

  // [11.1.16] У снимков ОДИН хозяин — этот модуль. Раньше здесь стоял
  // clone-хак «клонируем чтобы убрать старые listeners» — защита от второго
  // слушателя из toolBindings. Она не работала: initExportModule зовётся в
  // uiManager РАНЬШЕ initToolBindings (стр. 339 против 341), клон снимал
  // пустоту, а toolBindings вешал второго поверх — один щелчок скачивал ДВА
  // файла (у «Анонимного» — с двумя разными анонимизациями). Привязка пары в
  // toolBindings снята (см. надгробие там же), хак больше не нужен: прямой
  // слушатель, и код честно говорит, кто владелец.
  if (btnScreenshot) {
    btnScreenshot.addEventListener('click', () => downloadScreenshot({ includeOverlays: true }), { signal: _ac.signal });
  }

  if (btnScreenshotAnon) {
    btnScreenshotAnon.addEventListener('click', () => downloadScreenshot({ anonymous: true, includeOverlays: true }), { signal: _ac.signal });
  }

  // MP4 видео
  if (btnExportMp4) {
    const newBtn = btnExportMp4.cloneNode(true);
    btnExportMp4.parentNode.replaceChild(newBtn, btnExportMp4);
    newBtn.addEventListener('click', _showVideoDialog, { signal: _ac.signal });
  }

  // PDF кнопка — если есть vrt-btn-export
  const btnVrtExport = $('vrt-btn-export');
  if (btnVrtExport) {
    btnVrtExport.addEventListener('click', () => exportPDF({ allViewports: true }), { signal: _ac.signal });
  }
}

function _showVideoDialog() {
  if (_isRecording) {
    stopVideoRecording();
    showToast('⏹️ Запись остановлена', 'info');
    return;
  }

  // Простой диалог выбора режима
  const existing = $('export-video-dialog');
  if (existing) existing.remove();

  const dlg = document.createElement('div');
  dlg.id = 'export-video-dialog';
  dlg.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:999999;' +
    'background:#13132a;border:1px solid #2a2a40;border-radius:10px;padding:20px;color:#e0e0e0;font-family:system-ui;' +
    'box-shadow:0 8px 32px rgba(0,0,0,0.6);min-width:260px;';

  dlg.innerHTML = `
    <h3 style="margin:0 0 12px;font-size:15px;">🎥 Запись видео</h3>
    <div style="display:flex;flex-direction:column;gap:8px;">
      <button id="vid-scroll" style="padding:10px;background:#2563eb;border:none;color:#fff;border-radius:6px;cursor:pointer;font-size:13px;">
        📜 Прокрутка серии
      </button>
      <button id="vid-rotate" style="padding:10px;background:#7c3aed;border:none;color:#fff;border-radius:6px;cursor:pointer;font-size:13px;">
        🔄 Вращение 3D (10 сек)
      </button>
      <button id="vid-manual" style="padding:10px;background:#059669;border:none;color:#fff;border-radius:6px;cursor:pointer;font-size:13px;">
        🖱️ Ручная запись (стоп по кнопке)
      </button>
      <button style="padding:8px;background:#333;border:none;color:#888;border-radius:6px;cursor:pointer;font-size:12px;" onclick="this.closest('#export-video-dialog').remove()">
        Отмена
      </button>
    </div>
  `;

  document.body.appendChild(dlg);

  $('vid-scroll')?.addEventListener('click', () => { dlg.remove(); startVideoRecording({ mode: 'scroll' }); }, { signal: _ac.signal });
  $('vid-rotate')?.addEventListener('click', () => { dlg.remove(); startVideoRecording({ mode: 'rotate' }); }, { signal: _ac.signal });
  $('vid-manual')?.addEventListener('click', () => { dlg.remove(); startVideoRecording({ mode: 'manual' }); }, { signal: _ac.signal });
}

// ══════════════════════════════════════════════════════════════
// UTILS
// ══════════════════════════════════════════════════════════════

function _delay(ms) { return new Promise(r => setTimeout(r, ms)); }

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ══════════════════════════════════════════════════════════════
// 5. CLIPBOARD — копировать скриншот в буфер обмена
// ══════════════════════════════════════════════════════════════

/**
 * Копирует текущий viewport в буфер обмена как PNG.
 * @param {Object} [opts] — те же opts что captureViewport
 */
export async function copyToClipboard(opts = {}) {
  отметитьЭкспорт('буфер', opts?.filename || opts?.name || '');
  try {
    const blob = await captureViewport({ ...opts, format: 'png' });
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob })
    ]);
    showToast('📋 Скриншот скопирован в буфер', 'success', 2000);
  } catch (e) {
    // Fallback: открыть в новой вкладке
    log.warn('[Export] Clipboard write failed, opening in tab:', e.message);
    const url = URL.createObjectURL(await captureViewport({ ...opts, format: 'png' }));
    window.open(url, '_blank');
    showToast('📋 Буфер не доступен — открыто в новой вкладке', 'info', 3000);
  }
}

// ══════════════════════════════════════════════════════════════
// 6. GIF — анимированная прокрутка серии
// ══════════════════════════════════════════════════════════════

/**
 * Экспорт серии как анимированный GIF (через gif.js CDN).
 * @param {Object} opts
 * @param {number} [opts.fps=8]
 * @param {number} [opts.quality=10] — 1 (best) to 30 (worst)
 * @param {number} [opts.step=1] — каждый N-й срез
 * @param {number} [opts.scale=0.5] — масштаб (0.25-1.0)
 */
export async function exportAsGif(opts = {}) {
  отметитьЭкспорт('GIF', opts?.filename || opts?.name || '');
  try {
    const { fps = 8, quality = 10, step = 1, scale = 0.5 } = opts;
  
    const vpId = _ctx?.getCurrentViewportId?.() || getViewportIds()[0];
    const re = getRenderingEngine();
    const vp = re.getViewport(vpId);
    if (!vp) { showToast('❌ Окно не найден', 'error'); return; }
  
    const vpEl = $(`viewport-${vpId}`) || $('viewport-container');
    const glCanvas = vpEl?.querySelector('canvas');
    if (!glCanvas) { showToast('❌ Холст не найден', 'error'); return; }
  
    // [12.0.52] Кадры — равным шагом по ВСЕЙ серии, не больше 200 (gifFrames.js).
    const total = vp.getNumberOfSlices?.() || 1;
    const { планКадров } = await import('../core/gifFrames.js');
    const план = планКадров(total, { шаг: step, предел: 200 });
    if (план.индексы.length < 2) { showToast('GIF: в окне один срез — листать нечего', 'warning', 4000); return; }
    showToast(`🎞 Создание GIF (${план.индексы.length} кадров${план.шаг > 1 ? `, каждый ${план.шаг}-й срез` : ''})...`, 'info', 3000);
  
    try {
      // [10.19.10] Была загрузка с jsdelivr — то же самое: пакет gif.js стоит
      // локально. Плюс рабочий файл теперь берётся у сборщика, а не по адресу
      // '/libs/...', которого не существует.
      const GIF = (await import('gif.js')).default;
      const { АДРЕС_РАБОЧЕГО_ФАЙЛА } = await import('../core/gifWorkerUrl.js');
      
      const w = Math.round(glCanvas.width * scale);
      const h = Math.round(glCanvas.height * scale);
      
      const gif = new GIF({
        workers: 2,
        quality,
        width: w,
        height: h,
        workerScript: АДРЕС_РАБОЧЕГО_ФАЙЛА,
      });
  
      const offscreen = document.createElement('canvas');
      offscreen.width = w;
      offscreen.height = h;
      const offCtx = offscreen.getContext('2d');
  
      // [12.0.52] Переход к срезу — csUtils.jumpToSlice, он работает и для
      // стопки, и для окон MPR. Прежде звался vp.setImageIdIndex, которого у
      // объёмного окна нет: вызов через ?. молча пропускался, и GIF из окна
      // MPR состоял из одинаковых кадров. Ещё он не ждался (у стопки он
      // асинхронный) — кадр мог сняться до подгрузки снимка.
      const прыжок = cornerstone?.utilities?.jumpToSlice;
      const исходный = vp.getSliceIndex?.() ?? vp.getCurrentImageIdIndex?.() ?? 0;
      const кадр = () => new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      const перейти = async (i) => {
        try {
          if (typeof прыжок === 'function' && vp.element) await прыжок(vp.element, { imageIndex: i });
          else if (typeof vp.setImageIdIndex === 'function') await vp.setImageIdIndex(i);
          vp.render();
        } catch (e) { log.debug("[exportModule]", e.message); }
        await кадр();
      };
      for (let n = 0; n < план.индексы.length; n++) {
        await перейти(план.индексы[n]);
        offCtx.drawImage(glCanvas, 0, 0, w, h);
        gif.addFrame(offCtx, { copy: true, delay: Math.round(1000 / fps) });
        if (n % 20 === 0) showToast(`🎞 GIF: ${Math.round(n / план.индексы.length * 100)}%`, 'info', 500);
      }
      await перейти(исходный);   // вернуть срез, на котором стоял врач
  
      return new Promise((resolve) => {
        gif.on('finished', (blob) => {
          downloadBlob(blob, `mikhmel_${Date.now()}.gif`);
          showToast(`🎞 GIF сохранён (${(blob.size / 1024 / 1024).toFixed(1)} МБ)`, 'success');
          resolve(blob);
        });
        gif.render();
      });
  
    } catch (e) {
      log.warn('[Export] GIF library not available:', e.message);
      showToast('❌ Для GIF нужен gif.js — используйте видео-экспорт', 'error');
    }
  } catch (e) { log.warn(`[exportModule] exportAsGif failed:`, e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
// 7. COMPARISON — 2 viewport рядом
// ══════════════════════════════════════════════════════════════

/**
 * Экспорт двух viewport рядом для сравнения.
 * @param {string} vpId1
 * @param {string} vpId2
 * @param {Object} [opts]
 */
export async function exportComparison(vpId1, vpId2, opts = {}) {
  отметитьЭкспорт('сравнение', opts?.filename || opts?.name || '');
  try {
    const blob1 = await captureViewport({ ...opts, viewportId: vpId1 });
    const blob2 = await captureViewport({ ...opts, viewportId: vpId2 });
  
    const img1 = await _blobToImage(blob1);
    const img2 = await _blobToImage(blob2);
  
    const gap = 4;
    const w = img1.width + img2.width + gap;
    const h = Math.max(img1.height, img2.height);
  
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img1, 0, 0);
    ctx.drawImage(img2, img1.width + gap, 0);
  
    // Labels
    ctx.font = 'bold 14px system-ui';
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(4, 4, 80, 22);
    ctx.fillRect(img1.width + gap + 4, 4, 80, 22);
    ctx.fillStyle = '#4f8cff';
    ctx.fillText(vpId1, 8, 20);
    ctx.fillText(vpId2, img1.width + gap + 8, 20);
  
    return new Promise(resolve => {
      canvas.toBlob((blob) => {
        downloadBlob(blob, `comparison_${Date.now()}.png`);
        showToast('🔀 Сравнение сохранено', 'success');
        resolve(blob);
      });
    });
  } catch (e) { log.warn(`[exportModule] exportComparison failed:`, e.message); return null; }
}

function _blobToImage(blob) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(img.src); resolve(img); };
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
}

// ══════════════════════════════════════════════════════════════
// 8. DICOM INFO REPORT — текстовый отчёт с метаданными
// ══════════════════════════════════════════════════════════════

/**
 * Генерирует текстовый отчёт со всеми DICOM тегами и скриншотом.
 */
export async function exportDICOMReport(opts = {}) {
  try {
    const { anonymous = false } = opts;

    showToast('📋 Генерация DICOM-отчёта...', 'info', 2000);

    // [11.1.18] Три починки разом. (1) Селектор был #dicom-tags-content —
    // такого id в разметке НЕТ (модалка — modal-tags-content): отчёт всегда
    // видел ноль тегов. (2) Даже с верным id модалка пуста, пока её не
    // открывали, — теги рендерятся по требованию; поэтому сперва зовём
    // renderDICOMTags из sharedCtx. (3) Метка истории была «DICOM SR» —
    // чужое имя для отчёта по тегам; и ставилась ДО работы, даже на пустой
    // провал. Теперь метка честная и только при настоящем файле; пустышку
    // не скачиваем (владелец получил такой файл — вложил к замечаниям).
    try { await _ctx?.renderDICOMTags?.(); } catch (e) { /* без тегов скажем ниже */ }

    const tags = [];
    document.querySelectorAll('#modal-tags-content .tag-row').forEach(row => {
      const cells = row.querySelectorAll('.tag-id, .tag-vr, .tag-val');
      if (cells.length >= 3) {
        const tagId = cells[0].textContent.trim();
        const vr = cells[1].textContent.trim();
        let val = cells[2].textContent.trim();
        if (anonymous) val = _anonymizeText(val);
        tags.push({ tagId, vr, val });
      }
    });
  
    let report = `DICOM Report — Mikhmel Cornerstone4D Viewer\n`;
    report += `Дата: ${new Date().toLocaleString('ru-RU')}\n`;
    report += `${'═'.repeat(60)}\n\n`;
  
    if (tags.length > 0) {
      report += `DICOM Tags (${tags.length}):\n`;
      report += `${'─'.repeat(60)}\n`;
      for (const t of tags) {
        report += `${t.tagId}  ${t.vr}  ${t.val}\n`;
      }
    } else {
      showToast('DICOM теги не найдены — откройте исследование, затем повторите', 'warning', 3500);
      return;   // [11.1.18] пустой файл-обманку не скачиваем
    }
  
    report += `\n${'═'.repeat(60)}\n`;
    report += `Сгенерировано: Mikhmel DICOM Viewer v5\n`;
  
    const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
    const имяФайла = `dicom_report_${Date.now()}.txt`;
    downloadBlob(blob, имяФайла);
    отметитьЭкспорт('DICOM отчёт', имяФайла, blob.size / 1048576);
    showToast(`📋 Отчёт сохранён (${tags.length} тегов)`, 'success');
  } catch (e) { log.warn(`[exportModule] exportDICOMReport failed:`, e.message);  }
}

// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Real functionality
// ══════════════════════════════════════════════════════════════

/**
 * [11.1.18] Открыть печатный HTML тремя дверями по старшинству:
 * настольная (IPC printToPDF → диалог сохранения), браузерная (window.open
 * blob — в Electron запрещена и вернёт null), запасная (printUtil: iframe +
 * системная печать, где человек выбирает «Сохранить как PDF»). Возвращает,
 * какая дверь сработала: 'desktop' | 'window' | 'iframe' | 'none'.
 */
export async function открытьПечатное(html, размеры = 'width=800,height=900', открытьПосле = false) {
  const стол = window.MikhmelDesktop?.printReportPdf;
  if (стол) {
    const р = await стол(html, открытьПосле);   // [11.1.30] «открывать после сохранения»
    if (р?.saved) { showToast(открытьПосле ? '📄 PDF сохранён и открыт: ' + р.path : '📄 PDF сохранён: ' + р.path, 'success', 3500); return 'desktop'; }
    if (р && !р.error) { showToast('Сохранение отменено', 'info', 1500); return 'desktop'; }
    showToast('❌ PDF не сохранился: ' + (р?.error || 'неизвестно'), 'error', 4000);
    // падаем в запаску ниже — пусть хоть печать откроется
  }
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank', размеры);
  if (w) { w.addEventListener('load', () => URL.revokeObjectURL(url)); return 'window'; }
  URL.revokeObjectURL(url);
  const via = openPrintWindow(html, window);
  if (via !== 'none') { showToast('Окна запрещены — открыт диалог печати: выберите «Сохранить как PDF»', 'info', 4000); }
  else showToast('❌ Не удалось открыть ни окно, ни печать', 'error', 4000);
  return via;
}

/** @type {Map<string, {blob:Blob, format:string, timestamp:number}>} */
const _exportHistory = new Map();
let _экспортСчёт = 0;
/** [10.19.43] ПИСАТЕЛЬ истории. Читатели и «Очистить» жили с рождения, а
 *  писателя не было ни одного (_exportHistory.set — ноль вхождений); история
 *  вечно показывала «Пусто», очистка чистила пустоту. Решение владельца:
 *  провести. Экспортируется для точек вне этого модуля (лента, пакет срезов). */
export function отметитьЭкспорт(format, filename = '', sizeMB = null) {
  try {
    const id = ++_экспортСчёт;
    _exportHistory.set(String(id), { id, format, filename, sizeMB: sizeMB != null ? +(+sizeMB).toFixed(1) : null, timestamp: Date.now() });
    if (_exportHistory.size > 50) _exportHistory.delete(_exportHistory.keys().next().value);
  } catch (e) { /* история — не повод ронять экспорт */ }
}
const HISTORY_MAX = 20;

/**
 * Batch export all viewports as separate files with auto-naming
 * @param {Object} opts
 * @param {string} [opts.format='png']
 * @param {boolean} [opts.anonymous=false]
 * @param {string} [opts.prefix='viewport']
 * @returns {Promise<number>} count of exported files
 */
export async function batchExportAllViewports(opts = {}) {
  отметитьЭкспорт('все окна', opts?.filename || opts?.name || '');
  const re = getRenderingEngine();
  if (!re) { showToast('⚠️ Нет движок отрисовки', 'warning'); return 0; }
  const vpIds = getViewportIds();
  let count = 0;
  for (const vpId of vpIds) {
    try {
      const blob = await captureViewport({ ...opts, viewportId: vpId });
      if (blob) {
        downloadBlob(blob, `${opts.prefix || 'viewport'}_${vpId}_${Date.now()}.${opts.format || 'png'}`);
        count++;
      }
    } catch (e) { log.warn(`[Export] VP ${vpId}:`, e.message); }
  }
  showToast(`📸 Экспортировано ${count}/${vpIds.length} окно`, 'success', 2000);
  return count;
}

/**
 * Export with radiologist report template — structured PDF with findings/impression sections
 * @param {Object} opts
 * @param {string} opts.findings — описание
 * @param {string} opts.impression — заключение
 * @param {string} [opts.physician] — врач
 * @param {string} [opts.technique] — техника исследования
 * @param {string} [opts.comparison] — сравнение с предыдущим
 */
export async function exportRadiologistReport(opts = {}) {
  отметитьЭкспорт('отчёт', opts?.filename || opts?.name || '');
  const meta = _ctx?.getCurrentStudyMeta?.() || {};
  const patName = meta.patientName || 'Unknown';
  const studyDate = meta.studyDate || new Date().toLocaleDateString();
  // Capture current viewport for inline image
  let imgDataUrl = '';
  try {
    const blob = await captureViewport({ format: 'jpeg', quality: 0.8 });
    if (blob) imgDataUrl = URL.createObjectURL(blob);
  } catch (e) { log.debug("[exportModule]", e.message); }

  const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Радиологическое заключение</title>
  <style>
    @page{margin:20mm;size:A4} body{font-family:'Times New Roman',serif;font-size:12pt;max-width:700px;margin:0 auto;padding:20px;line-height:1.6;color:#222}
    h1{font-size:16pt;text-align:center;border-bottom:2px solid #333;padding-bottom:10px}
    h2{font-size:13pt;border-bottom:1px solid #ccc;margin-top:18px;color:#444}
    .meta{display:grid;grid-template-columns:1fr 1fr;gap:4px;background:#f5f5f5;padding:12px;border-radius:4px;margin:12px 0;font-size:11pt}
    .sig{margin-top:40px;border-top:1px solid #333;padding-top:8px;width:250px}
    .no-print{text-align:center;margin-bottom:16px}
    .img-block{text-align:center;margin:16px 0}
    .img-block img{max-width:100%;border:1px solid #ddd;border-radius:4px}
    @media print{.no-print{display:none}}
  </style></head><body>
  <div class="no-print"><button onclick="window.print()" style="padding:10px 30px;font-size:14px;background:#3b82f6;color:#fff;border:none;border-radius:8px;cursor:pointer">📄 Печать / Сохранить PDF</button></div>
  <h1>РАДИОЛОГИЧЕСКОЕ ЗАКЛЮЧЕНИЕ</h1>
  <div class="meta">
    <div><b>Пациент:</b> ${escapeHtml(patName)}</div>
    <div><b>Дата исследования:</b> ${escapeHtml(studyDate)}</div>
    <div><b>Модальность:</b> ${escapeHtml(meta.modality || '—')}</div>
    <div><b>Врач:</b> ${escapeHtml(opts.physician || '—')}</div>
  </div>
  ${opts.technique ? `<h2>МЕТОДИКА</h2><p>${escapeHtml(opts.technique)}</p>` : ''}
  ${opts.comparison ? `<h2>СРАВНЕНИЕ</h2><p>${escapeHtml(opts.comparison)}</p>` : ''}
  <h2>ОПИСАНИЕ</h2><div style="white-space:pre-wrap">${escapeHtml(opts.findings || '—')}</div>
  ${imgDataUrl ? `<div class="img-block"><img src="${imgDataUrl}" alt="viewport screenshot"></div>` : ''}
  <h2>ЗАКЛЮЧЕНИЕ</h2><div style="white-space:pre-wrap;font-weight:bold">${escapeHtml(opts.impression || '—')}</div>
  <div class="sig"><b>Врач:</b> ${escapeHtml(opts.physician || '___________')}<br><b>Дата:</b> ${new Date().toLocaleDateString()}</div>
  </body></html>`;
  // [11.1.18] Через общий открыватель: в Electron window.open запрещён
  // (setWindowOpenHandler → deny), и прежний код падал бы на
  // w.addEventListener от null. Открыватель идёт родной дверью printToPDF,
  // затем браузерной, затем iframe-печатью.
  await открытьПечатное(htmlContent, 'width=800,height=900');
  showToast('📄 Отчёт сформирован', 'success');
}

/**
 * [6.7.11] Структурированный отчёт ПО ИЗМЕРЕНИЯМ — таблица всех аннотаций
 * (длины/углы/ROI с HU-статистикой) + шапка пациента + снимок текущего окна.
 * Открывается в новом окне с кнопкой «Печать / Сохранить PDF». Значения берутся из
 * cachedStats аннотаций (после фикса калибровки 6.7.10 длины идут в мм).
 * @param {Object} opts
 * @param {string} [opts.physician] — врач (необязательно)
 */
export async function exportMeasurementsReport(opts = {}) {
  const meta = _ctx?.getCurrentStudyMeta?.() || {};
  const patName = meta.patientName || '—';
  const rawDate = meta.studyDate || '';
  const studyDate = /^\d{8}$/.test(rawDate)
    ? `${rawDate.slice(6, 8)}.${rawDate.slice(4, 6)}.${rawDate.slice(0, 4)}`
    : (rawDate || new Date().toLocaleDateString('ru-RU'));
  const modality = meta.modality || '—';

  let anns = [];
  try { anns = cornerstoneTools?.annotation?.state?.getAllAnnotations?.() || []; }
  catch (e) { log.warn('[Export] getAllAnnotations:', e.message); }

  const TOOL_RU = {
    Length: 'Длина', Height: 'Высота', Angle: 'Угол', CobbAngle: 'Угол Кобба',
    Bidirectional: 'Двунаправленное', EllipticalROI: 'Эллипс (ROI)',
    RectangleROI: 'Прямоугольник (ROI)', CircleROI: 'Круг (ROI)', Probe: 'Точка (HU)',
    ArrowAnnotate: 'Стрелка', PlanarFreehandROI: 'Произвольная (ROI)', SplineROI: 'Сплайн (ROI)',
  };

  const rows = anns.map((a) => {
    const toolRaw = a.metadata?.toolName || a.annotationType || '—';
    const label = a.data?.label || a.data?.text || '';
    const slice = (a.metadata?.sliceIndex != null) ? (a.metadata.sliceIndex + 1) : '—';
    const stats = a.data?.cachedStats ? (Object.values(a.data.cachedStats)[0] || {}) : {};
    let value = '—';
    if (stats.length != null) value = `${(+stats.length).toFixed(1)} ${stats.unit || 'mm'}`;
    else if (a.data?.length != null) value = `${(+a.data.length).toFixed(1)} mm`;
    else if (stats.angle != null) value = `${(+stats.angle).toFixed(1)}°`;
    else if (a.data?.angle != null) value = `${(+a.data.angle).toFixed(1)}°`;
    else if (stats.area != null) value = `${(+stats.area).toFixed(1)} ${stats.areaUnit || 'mm²'}`;
    let details = '';
    if (stats.mean != null) {
      details = `сред ${Math.round(stats.mean)} HU`;
      if (stats.stdDev != null) details += ` ± ${Math.round(stats.stdDev)}`;
      if (stats.min != null && stats.max != null) details += ` (${Math.round(stats.min)}…${Math.round(stats.max)})`;
    }
    return { tool: TOOL_RU[toolRaw] || toolRaw, label, value, details, slice };
  });

  let imgDataUrl = '';
  try { const blob = await captureViewport({ format: 'jpeg', quality: 0.8 }); if (blob) imgDataUrl = URL.createObjectURL(blob); }
  catch (e) { log.debug('[Export]', e.message); }

  const rowsHtml = rows.length
    ? rows.map((r, i) => `<tr><td>${i + 1}</td><td>${escapeHtml(r.tool)}</td><td>${escapeHtml(r.label)}</td><td class="v">${escapeHtml(r.value)}</td><td class="d">${escapeHtml(r.details)}</td><td class="c">${escapeHtml(String(r.slice))}</td></tr>`).join('')
    : `<tr><td colspan="6" class="empty">Измерений нет — сделайте замеры инструментами (Длина, Угол, ROI) и повторите</td></tr>`;

  const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Отчёт по измерениям</title>
  <style>
    @page{margin:18mm;size:A4} body{font-family:'Segoe UI',Arial,sans-serif;font-size:11pt;max-width:760px;margin:0 auto;padding:20px;color:#1f2430}
    h1{font-size:16pt;text-align:center;border-bottom:2px solid #2b3550;padding-bottom:10px;margin-bottom:6px}
    .sub{text-align:center;color:#667;font-size:9pt;margin-bottom:14px}
    .meta{display:grid;grid-template-columns:1fr 1fr;gap:4px 20px;background:#f4f6fa;padding:12px 16px;border-radius:6px;margin:12px 0;font-size:10pt}
    table{width:100%;border-collapse:collapse;margin:14px 0;font-size:10pt}
    th{background:#2b3550;color:#fff;text-align:left;padding:7px 9px;font-weight:600}
    td{padding:6px 9px;border-bottom:1px solid #e3e7ef}
    tr:nth-child(even) td{background:#f8f9fc}
    td.v{font-weight:600;white-space:nowrap} td.d{color:#445;font-size:9.5pt} td.c{text-align:center;color:#667}
    .empty{text-align:center;color:#888;padding:18px}
    .img-block{text-align:center;margin:16px 0} .img-block img{max-width:100%;border:1px solid #dde;border-radius:6px}
    .foot{margin-top:20px;border-top:1px solid #cfd6e4;padding-top:8px;color:#889;font-size:8.5pt;display:flex;justify-content:space-between}
    .no-print{text-align:center;margin-bottom:16px}
    @media print{.no-print{display:none}}
  </style></head><body>
  <div class="no-print"><button onclick="window.print()" style="padding:10px 30px;font-size:14px;background:#2b3550;color:#fff;border:none;border-radius:8px;cursor:pointer">📄 Печать / Сохранить PDF</button></div>
  <h1>ОТЧЁТ ПО ИЗМЕРЕНИЯМ</h1>
  <div class="sub">Mikhmel Cornerstone4D DICOM Viewer</div>
  <div class="meta">
    <div><b>Пациент:</b> ${escapeHtml(patName)}</div>
    <div><b>Дата исследования:</b> ${escapeHtml(studyDate)}</div>
    <div><b>Модальность:</b> ${escapeHtml(modality)}</div>
    <div><b>Врач:</b> ${escapeHtml(opts.physician || '—')}</div>
  </div>
  <table>
    <thead><tr><th>№</th><th>Тип</th><th>Метка</th><th>Значение</th><th>HU / детали</th><th>Срез</th></tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  ${imgDataUrl ? `<div class="img-block"><img src="${imgDataUrl}" alt="снимок"></div>` : ''}
  <div class="foot"><span>Всего измерений: ${rows.length}</span><span>Сформировано: ${new Date().toLocaleString('ru-RU')}</span></div>
  </body></html>`;

  // [11.1.18] Через общий открыватель: в Electron window.open запрещён
  // (setWindowOpenHandler → deny) — идём родной дверью printToPDF, потом
  // браузерной, потом iframe-печатью.
  await открытьПечатное(htmlContent, 'width=860,height=940');
  showToast(`📊 Отчёт по измерениям: ${rows.length}`, 'success');
}

/**
 * Get export history (last N exports with metadata)
 * @returns {Array}
 */
export function getExportHistory() {
  // [11.1.18] Зеркало болезни 10.19.43: тогда не было ПИСАТЕЛЯ, теперь
  // читатель ждал data.blob.size — а писатель (отметитьЭкспорт) блобы не
  // хранит нарочно (полсотни экспортов в памяти — это гигабайты). Первый же
  // элемент ронял map TypeError'ом, и «История экспорта» выглядела вечно
  // пустой — второе замечание владельца. Читаем то, что пишется.
  return [..._exportHistory.values()].map((з) => ({
    id: з.id, format: з.format, filename: з.filename || '',
    sizeMB: з.sizeMB != null ? з.sizeMB : (з.blob ? +(з.blob.size / 1048576).toFixed(2) : null),
    когда: new Date(з.timestamp).toLocaleTimeString('ru-RU'),
    timestamp: new Date(з.timestamp).toLocaleTimeString(),
  }));
}

/**
 * Re-download a previous export by ID
 * @param {string} id
 */
export function redownloadExport(id) {
  const entry = _exportHistory.get(id);
  if (!entry) { showToast('❌ Экспорт не найден', 'error'); return; }
  downloadBlob(entry.blob, `re_${id}.${entry.format}`);
}

/**
 * Clear export history (free memory)
 */
export function clearExportHistory() {
  _exportHistory.clear();
  showToast('🗑 История экспорта очищена', 'info', 1200);
}

// Hook into captureViewport to auto-save to history
const _origCapture = captureViewport;
// Note: we can't reassign the export, but _exportHistory tracks via events

// [FIX] Cleanup all event listeners
export function destroyExportModule() {
  _ac.abort();
  _ac = new AbortController();
}
